import { useState, useEffect, createContext, useCallback, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import Cookies from 'js-cookie';
import { Crisp, ChatboxColors } from 'crisp-sdk-web';
import * as Sentry from '@sentry/react';
import { decode } from 'html-entities';
import { getRegions } from '@/lib/utils';
import { Region } from '@/types';

const userContext = createContext<UserContext | undefined>(undefined);

export type UserToken = {
    account: {
        id: string;
        value: {
            email: string;
            firstname: string;
            lastname: string;
            activated: boolean;
            language: string;
            creation_date: string;
        };
        crisp: {
            hash: string;
        };
    };
    region: string;
    role: number;
    token_type: number;
    iat: number;
    exp: number;
};

export type UserTokenValue = UserToken['account'];

export type UserContext = {
    user: UserToken | null;
    token: string | undefined;
    regions: Region[];
    setToken: (token?: string) => void;
    login: (token: string) => void;
    logout: () => void;
};

export function UserContextProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    //const { logout: logoutWS } = useApiService();
    const [user, setUser] = useState<UserToken | null>(null);
    const [token, setToken] = useState(Cookies.get('token'));

    const [regions, setRegions] = useState<Region[]>([]);

    Crisp.configure(import.meta.env.VITE_CRISP_WEBSITE_ID, {
        autoload: false,
    });

    const login = useCallback((token: string) => {
        setToken(token);

        const domain = window.location.hostname.replace('school', '');

        Cookies.set('token', token, { domain, expires: 4 });
    }, []);

    useEffect(() => {
        const cookieToken = Cookies.get('token');

        if (cookieToken) {
            setToken(cookieToken);
        }

        const retrieveRegions = async () => {
            const regions = (await getRegions()) as Region[];
            setRegions(regions);
        };
        retrieveRegions();
    }, []);

    useEffect(() => {
        const token = Cookies.get('token');

        if (token) {
            const user = jwtDecode(token) as UserToken;

            setUser(user);
        } else {
            setUser(null);
        }
    }, [token]);

    const logout = async () => {
        const domain = window.location.hostname.replace('school', '');
        Cookies.remove('user');
        Cookies.remove('token', { domain });

        setUser(null);
        setToken(undefined);
        Crisp.session.reset(true);
        navigate('/');
    };

    useEffect(() => {
        console.warn('User', user);
        if (user) {
            const userProperties: UserToken['account']['value'] = user?.account?.value;

            Crisp.load();

            Crisp.setColorTheme(ChatboxColors.Blue);
            Crisp.user.setEmail(userProperties.email);
            Crisp?.user?.setNickname(decode(`${userProperties.firstname} ${userProperties.lastname}`));
            Crisp?.session?.setData({
                user_id: user?.account?.id,
            });

            if (import.meta.env.VITE_SENTRY_DSN) {
                let username = '';
                if (userProperties.firstname || userProperties.lastname) {
                    username = `${userProperties.firstname} ${userProperties.lastname}`.trim();
                }

                Sentry.setUser({
                    id: user?.account.id,
                    email: userProperties.email,
                    ...(username !== '' ? { username } : {}),
                });
            }
        } else {
            Sentry.setUser(null);
            console.log('User is NOT logged in');
        }
    }, [user]);

    return (
        <userContext.Provider value={{ user, token, regions, logout, login, setToken }}>
            {children}
        </userContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useUserContext = () => {
    const context = useContext(userContext);
    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserContext');
    }
    return context;
};
